import React, { useEffect, useState, useCallback } from 'react';
import {
    Button, Card, Col, Modal,
    Popconfirm, Row, Select, Space, Table, Tabs, Tag, Alert, Empty, Spin, Checkbox,
    InputNumber, Divider
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined,
    ThunderboltOutlined, CalendarOutlined, AppstoreOutlined, CopyOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { getRequest, postRequest, deleteRequest } from 'api';
import { toast } from 'react-toastify';
import { getStoredWorkMonth } from 'utils/workMonthStorage';
import QuickRegisterEditor, {
    IncomeSchool,
    QuickRegister,
    COLLECT_BY_LABEL
} from './QuickRegisterEditor';

const { Option } = Select;

interface RevenueBlock {
    id: number;
    code: string;
    name: string;
}

const QuickRegisterPage: React.FC = () => {
    const [blocks, setBlocks] = useState<RevenueBlock[]>([]);
    const [incomeSources, setIncomeSources] = useState<IncomeSchool[]>([]);
    const [selectedBlockId, setSelectedBlockId] = useState<number | undefined>(undefined);
    const [selectedMonth, setSelectedMonth] = useState<number>(() => getStoredWorkMonth().month);
    const [selectedYear, setSelectedYear] = useState<number>(() => getStoredWorkMonth().year);

    // Lắng nghe thay đổi kỳ làm việc từ cấu hình
    useEffect(() => {
        const handleWorkMonthChange = (e: any) => {
            if (e?.detail) {
                setSelectedMonth(e.detail.month);
                setSelectedYear(e.detail.year);
            }
        };
        window.addEventListener('workMonthChanged', handleWorkMonthChange);
        return () => window.removeEventListener('workMonthChanged', handleWorkMonthChange);
    }, []);

    const [currentRegister, setCurrentRegister] = useState<QuickRegister | null>(null);
    const [loading, setLoading] = useState(false);
    const [activeTabKey, setActiveTabKey] = useState<string>('0');

    // View Mode & Edit State
    const [viewMode, setViewMode] = useState<'LIST' | 'FORM'>('LIST');
    const [editingRegister, setEditingRegister] = useState<boolean>(false);

    // Modal Apply Register
    const [applyModalVisible, setApplyModalVisible] = useState(false);
    const [selectedApplyLevelId, setSelectedApplyLevelId] = useState<number | undefined>(undefined);
    const [applying, setApplying] = useState(false);
    const [copyingPrevious, setCopyingPrevious] = useState<boolean>(false);

    // Modal Copy From Other Register
    const [copyModalVisible, setCopyModalVisible] = useState<boolean>(false);
    const [copySourceBlockId, setCopySourceBlockId] = useState<number | undefined>(undefined);
    const [copySourceMonth, setCopySourceMonth] = useState<number>(() => (getStoredWorkMonth().month === 1 ? 12 : getStoredWorkMonth().month - 1));
    const [copySourceYear, setCopySourceYear] = useState<number>(() => (getStoredWorkMonth().month === 1 ? getStoredWorkMonth().year - 1 : getStoredWorkMonth().year));
    const [loadingCopySource, setLoadingCopySource] = useState<boolean>(false);
    const [copySourceRegister, setCopySourceRegister] = useState<QuickRegister | null>(null);
    const [hasSearchedCopySource, setHasSearchedCopySource] = useState<boolean>(false);
    const [copyingFromSource, setCopyingFromSource] = useState<boolean>(false);

    // Initial Data Fetch
    useEffect(() => {
        const initData = async () => {
            const schoolId = localStorage.getItem('schoolId');
            if (!schoolId) return;

            try {
                // Fetch blocks
                const blockRes = await getRequest(`/cms/accountant/revenue-blocks?schoolId=${schoolId}&status=1`);
                if (blockRes?.code === 200 && Array.isArray(blockRes.info)) {
                    setBlocks(blockRes.info);
                    if (blockRes.info.length > 0) {
                        setSelectedBlockId(blockRes.info[0].id);
                        setCopySourceBlockId(blockRes.info[0].id);
                    }
                }

                // Fetch income sources
                const incRes = await getRequest(`/cms/accountant/income-school?schoolId=${schoolId}&page=1&perPage=100`);
                if (incRes?.code === 200 && Array.isArray(incRes.info?.list)) {
                    setIncomeSources(incRes.info.list);
                }
            } catch (err) {
                console.error(err);
            }
        };

        initData();
    }, []);

    // Fetch Quick Register when block, month, year changes
    const fetchRegister = useCallback(async () => {
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId || !selectedBlockId) return;

        setLoading(true);
        try {
            const res = await getRequest(
                `/cms/accountant/quick-registers?schoolId=${schoolId}&month=${selectedMonth}&year=${selectedYear}&revenueBlockId=${selectedBlockId}`
            );
            if (res?.code === 200 && Array.isArray(res.info) && res.info.length > 0) {
                setCurrentRegister(res.info[0]);
                setActiveTabKey('0');
            } else {
                setCurrentRegister(null);
            }
        } catch (err) {
            toast.error('Lỗi khi tải thông tin phiếu đăng ký nhanh');
        } finally {
            setLoading(false);
        }
    }, [selectedBlockId, selectedMonth, selectedYear]);

    useEffect(() => {
        if (selectedBlockId) {
            fetchRegister();
        }
    }, [selectedBlockId, fetchRegister]);



    // Handle Delete Quick Register
    const handleDeleteRegister = async () => {
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId || !currentRegister) return;

        try {
            const res = await deleteRequest(`/cms/accountant/quick-registers/${currentRegister.id}?schoolId=${schoolId}`);
            if (res?.code === 200) {
                toast.success('Xóa phiếu thành công');
                setCurrentRegister(null);
            } else {
                toast.error(res?.msg || 'Xóa phiếu thất bại');
            }
        } catch (err: any) {
            toast.error(err?.message || 'Lỗi khi xóa');
        }
    };

    // Handle Apply Quick Register
    const handleApplyRegister = async () => {
        const schoolId = localStorage.getItem('schoolId');
        const targetLevelId = selectedApplyLevelId || currentRegister?.levels?.[0]?.id;
        if (!schoolId || !currentRegister) {
            toast.warn('Không tìm thấy phiếu đăng ký nhanh để áp dụng');
            return;
        }

        setApplying(true);
        try {
            const res = await postRequest(`/cms/accountant/quick-registers/${currentRegister.id}/apply?schoolId=${schoolId}`, {
                levelId: targetLevelId
            });

            if (res?.code === 200) {
                toast.success(res.msg || 'Áp dụng đăng ký nhanh toàn trường thành công!');
                setApplyModalVisible(false);
                fetchRegister();
            } else {
                toast.error(res?.msg || 'Áp dụng thất bại');
            }
        } catch (err: any) {
            toast.error(err?.message || 'Lỗi khi áp dụng');
        } finally {
            setApplying(false);
        }
    };

    // Handle Copy from Previous Month
    const handleCopyPreviousMonth = async () => {
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId) return;

        const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
        const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;

        setCopyingPrevious(true);
        try {
            let url = `/cms/accountant/quick-registers/copy-previous?schoolId=${schoolId}&month=${selectedMonth}&year=${selectedYear}`;
            if (selectedBlockId) {
                url += `&revenueBlockId=${selectedBlockId}`;
            }
            const res = await postRequest(url, {});
            if (res?.code === 200) {
                toast.success(res.msg || `Sao chép thành công phiếu đăng ký từ Tháng ${prevMonth}/${prevYear}!`);
                fetchRegister();
            } else {
                toast.error(res?.msg || 'Sao chép thất bại');
            }
        } catch (err: any) {
            toast.error(err?.message || 'Lỗi khi sao chép từ tháng trước');
        } finally {
            setCopyingPrevious(false);
        }
    };

    // Tìm kiếm cấu hình nguồn để sao chép
    const handleFindCopySource = async (bId = copySourceBlockId, m = copySourceMonth, y = copySourceYear) => {
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId || !bId) return;

        setLoadingCopySource(true);
        setHasSearchedCopySource(true);
        try {
            const res = await getRequest(
                `/cms/accountant/quick-registers?schoolId=${schoolId}&month=${m}&year=${y}&revenueBlockId=${bId}`
            );
            if (res?.code === 200 && Array.isArray(res.info) && res.info.length > 0) {
                setCopySourceRegister(res.info[0]);
            } else {
                setCopySourceRegister(null);
            }
        } catch (err) {
            toast.error('Lỗi khi tải thông tin phiếu nguồn!');
            setCopySourceRegister(null);
        } finally {
            setLoadingCopySource(false);
        }
    };

    // Thực thi sao chép từ nguồn thu khác sang kỳ & khối hiện tại
    const handleExecuteCopyFromSource = async () => {
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId || !copySourceBlockId || !selectedBlockId) {
            toast.warn('Vui lòng chọn khối thu nguồn và khối thu đích!');
            return;
        }

        setCopyingFromSource(true);
        try {
            const url = `/cms/accountant/quick-registers/copy-from?schoolId=${schoolId}&fromMonth=${copySourceMonth}&fromYear=${copySourceYear}&fromRevenueBlockId=${copySourceBlockId}&toMonth=${selectedMonth}&toYear=${selectedYear}&toRevenueBlockId=${selectedBlockId}`;
            const res = await postRequest(url, {});
            if (res?.code === 200) {
                toast.success(res.msg || 'Sao chép phiếu đăng ký nhanh thành công!');
                setCopyModalVisible(false);
                fetchRegister();
            } else {
                toast.error(res?.msg || 'Sao chép thất bại');
            }
        } catch (err: any) {
            toast.error(err?.message || 'Lỗi khi sao chép từ nguồn thu khác');
        } finally {
            setCopyingFromSource(false);
        }
    };

    const formatCurrency = (val?: number) => {
        return (val || 0).toLocaleString('vi-VN') + ' đ';
    };

    // Render Màn hình Lập / Sửa Phiếu Đăng Ký Nhanh (Full View)
    if (viewMode === 'FORM') {
        const blockName = blocks.find((b) => b.id === selectedBlockId)?.name || 'Khối thu';
        return (
            <QuickRegisterEditor
                initialData={editingRegister ? currentRegister : null}
                revenueBlockId={selectedBlockId!}
                blockName={blockName}
                month={selectedMonth}
                year={selectedYear}
                incomeSources={incomeSources}
                blocks={blocks}
                onBack={() => setViewMode('LIST')}
                onSaveSuccess={() => {
                    setViewMode('LIST');
                    fetchRegister();
                }}
            />
        );
    }

    return (
        <div style={{ padding: '4px 0' }}>
            {/* Thanh bộ lọc & thao tác chính chuẩn hệ thống, căn đều trên 1 hàng */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <Space size={16} align="center" wrap>
                    <Space size={6} align="center">
                        <span style={{ fontWeight: 500, color: '#595959' }}>Kỳ làm việc:</span>
                        <Tag
                            color="blue"
                            style={{
                                fontSize: 13,
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontWeight: 600,
                                margin: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4
                            }}
                        >
                            <CalendarOutlined /> Tháng {selectedMonth}/{selectedYear}
                        </Tag>
                    </Space>
                    <Space size={6} align="center">
                        <span style={{ fontWeight: 500, color: '#595959' }}>Khối thu:</span>
                        <Select
                            value={selectedBlockId}
                            onChange={(val) => setSelectedBlockId(val)}
                            style={{ width: 220 }}
                            placeholder="Chọn khối thu"
                        >
                            {blocks.map((b) => (
                                <Option key={b.id} value={b.id}>
                                    {b.name} ({b.code})
                                </Option>
                            ))}
                        </Select>
                    </Space>
                </Space>

                <Space align="center" wrap>
                    {selectedBlockId && (
                        <Button
                            icon={<CopyOutlined />}
                            onClick={() => {
                                setCopyModalVisible(true);
                                handleFindCopySource(copySourceBlockId || selectedBlockId, copySourceMonth, copySourceYear);
                            }}
                        >
                            Sao chép từ nguồn khác
                        </Button>
                    )}
                    {!currentRegister && selectedBlockId && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setEditingRegister(false);
                                setViewMode('FORM');
                            }}
                        >
                            Tạo phiếu mới
                        </Button>
                    )}
                    {currentRegister && currentRegister.status === 0 && (
                        <>
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => {
                                    setEditingRegister(true);
                                    setViewMode('FORM');
                                }}
                            >
                                Sửa phiếu
                            </Button>
                            <Popconfirm
                                title="Xác nhận xóa phiếu"
                                description="Bạn có chắc muốn xóa phiếu đăng ký nhanh này?"
                                onConfirm={handleDeleteRegister}
                                okText="Xóa"
                                cancelText="Hủy"
                                okButtonProps={{ danger: true }}
                            >
                                <Button danger icon={<DeleteOutlined />}>
                                    Xóa
                                </Button>
                            </Popconfirm>
                            <Button
                                type="primary"
                                style={{ backgroundColor: '#52c41a' }}
                                icon={<ThunderboltOutlined />}
                                onClick={() => {
                                    if (currentRegister.levels?.length > 0) {
                                        setSelectedApplyLevelId(currentRegister.levels[0].id);
                                    }
                                    setApplyModalVisible(true);
                                }}
                            >
                                Áp dụng toàn trường
                            </Button>
                        </>
                    )}
                    {currentRegister && currentRegister.status === 1 && (
                        <Tag color="cyan" icon={<CheckCircleOutlined />}>
                            Đã khóa (Áp dụng: {currentRegister.appliedDate ? new Date(currentRegister.appliedDate).toLocaleDateString('vi-VN') : ''})
                        </Tag>
                    )}
                </Space>
            </div>

            {/* Khung hiển thị Phiếu thu */}
            <Spin spinning={loading}>
                {currentRegister ? (
                    <Card
                        title={
                            <Space size="middle" align="center">
                                <span style={{ fontSize: 16, fontWeight: 600 }}>{currentRegister.name}</span>
                                <Tag color={currentRegister.status === 1 ? 'green' : 'orange'}>
                                    {currentRegister.status === 1 ? 'Đã áp dụng toàn trường' : 'Bản nháp (Chưa áp dụng)'}
                                </Tag>
                            </Space>
                        }
                    >
                        {/* Hiển thị trực tiếp bảng danh sách nguồn thu của khối thu */}
                        {currentRegister.levels && currentRegister.levels.length > 0 ? (
                            <div>
                                <Table
                                    rowKey={(r) => r.id || r.incomeSchoolId}
                                    dataSource={currentRegister.levels[0].items || []}
                                    pagination={false}
                                    bordered
                                    size="middle"
                                    rowClassName={(record) => (record.isDefault !== false ? 'ant-table-row-selected' : '')}
                                    columns={[
                                        {
                                            title: '',
                                            dataIndex: 'isDefault',
                                            key: 'isDefault',
                                            width: 55,
                                            align: 'center',
                                            render: (val) => (
                                                <Checkbox checked={val !== false} disabled />
                                            )
                                        },
                                        {
                                            title: 'Mã khoản',
                                            dataIndex: 'incomeSchoolCode',
                                            key: 'incomeSchoolCode',
                                            width: 120,
                                            render: (val) => val ? <Tag color="blue">{val}</Tag> : '-'
                                        },
                                        {
                                            title: 'Tên khoản thu',
                                            dataIndex: 'incomeSchoolName',
                                            key: 'incomeSchoolName',
                                            render: (val, record) => {
                                                const inc = incomeSources.find(i => i.id === record.incomeSchoolId);
                                                return <span style={{ fontWeight: record.isDefault !== false ? 600 : 400 }}>{val || inc?.name}</span>;
                                            }
                                        },
                                        {
                                            title: 'Hình thức',
                                            key: 'collectBy',
                                            width: 120,
                                            align: 'center',
                                            render: (_, record) => {
                                                const inc = incomeSources.find(i => i.id === record.incomeSchoolId);
                                                const cBy = (record as any)?.collectBy || (inc as any)?.collectBy || (inc as any)?.collect_by || 1;
                                                const tag = COLLECT_BY_LABEL[cBy] || { text: 'Theo tháng', color: 'blue' };
                                                return <Tag color={tag.color}>{tag.text}</Tag>;
                                            }
                                        },
                                        {
                                            title: 'Số lượng / Suất',
                                            dataIndex: 'units',
                                            key: 'units',
                                            width: 140,
                                            align: 'center',
                                            render: (val) => <b>{val}</b>
                                        },
                                        {
                                            title: 'Đơn giá',
                                            dataIndex: 'unitPrice',
                                            key: 'unitPrice',
                                            width: 160,
                                            align: 'right',
                                            render: (val) => formatCurrency(val)
                                        },
                                        {
                                            title: 'Thành tiền',
                                            dataIndex: 'amount',
                                            key: 'amount',
                                            width: 180,
                                            align: 'right',
                                            render: (val, record) =>
                                                record.isDefault !== false ? (
                                                    <b style={{ color: '#1677ff' }}>{formatCurrency(val)}</b>
                                                ) : (
                                                    <span style={{ color: '#8c8c8c' }}>{formatCurrency(val)}</span>
                                                )
                                        }
                                    ]}
                                    summary={(pageData) => {
                                        const total = pageData
                                            .filter((r) => r.isDefault !== false)
                                            .reduce((sum, r) => sum + (r.amount || 0), 0);
                                        return (
                                            <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                                                <Table.Summary.Cell index={0} colSpan={6}>
                                                    <span style={{ fontWeight: 600 }}>Tổng cộng:</span>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={1} align="right">
                                                    <span style={{ fontWeight: 700, fontSize: 15, color: '#f5222d' }}>
                                                        {formatCurrency(total)}
                                                    </span>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        );
                                    }}
                                />
                            </div>
                        ) : (
                            <Empty description="Phiếu chưa có khoản thu nào" />
                        )}
                    </Card>
                ) : (
                    <Card style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Empty
                            description={`Chưa có phiếu đăng ký nhanh cho ${blocks.find(b => b.id === selectedBlockId)?.name || 'khối thu này'} trong Tháng ${selectedMonth}/${selectedYear}`}
                        >
                            <Space style={{ marginTop: 16 }}>
                                <Button
                                    icon={<CopyOutlined />}
                                    style={{ color: '#fa8c16', borderColor: '#fa8c16', fontWeight: 500 }}
                                    loading={copyingPrevious}
                                    onClick={handleCopyPreviousMonth}
                                >
                                    Sao chép từ tháng trước
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => {
                                        setEditingRegister(false);
                                        setViewMode('FORM');
                                    }}
                                >
                                    Tạo phiếu mới
                                </Button>
                            </Space>
                        </Empty>
                    </Card>
                )}
            </Spin>



            {/* Modal Áp Dụng Đăng Ký Toàn Trường */}
            <Modal
                title="Xác nhận Áp dụng Đăng ký nhanh toàn trường"
                open={applyModalVisible}
                onCancel={() => setApplyModalVisible(false)}
                onOk={handleApplyRegister}
                confirmLoading={applying}
                okText="Áp dụng ngay"
                cancelText="Hủy"
                okButtonProps={{ style: { backgroundColor: '#52c41a' } }}
            >
                <div style={{ padding: '8px 0' }}>
                    <Alert
                        message="Cơ chế áp dụng đồng loạt"
                        description={
                            <div>
                                - Tự động quét toàn bộ học sinh thuộc khối thu <b>{blocks.find(b => b.id === selectedBlockId)?.name}</b> trong toàn trường.<br />
                                - Học sinh đang có cờ <b>Tự đăng ký</b> trong tháng sẽ được <b>tự động bỏ qua</b>.<br />
                                - Các khoản thu đã xuất hóa đơn/biên lai sẽ được <b>giữ nguyên bảo vệ</b>.<br />
                                - Sau khi áp dụng thành công, phiếu thu sẽ được <b>khóa lại</b>.
                            </div>
                        }
                        type="warning"
                        showIcon
                        style={{ marginBottom: 0 }}
                    />
                </div>
            </Modal>

            {/* Modal: Sao chép từ nguồn thu khác */}
            <Modal
                title={
                    <Space>
                        <CopyOutlined style={{ color: '#1677ff' }} />
                        <span style={{ fontWeight: 600 }}>Sao chép cấu hình từ nguồn khác</span>
                    </Space>
                }
                open={copyModalVisible}
                onCancel={() => setCopyModalVisible(false)}
                width={650}
                footer={[
                    <Button key="cancel" onClick={() => setCopyModalVisible(false)}>
                        Đóng
                    </Button>,
                    <Button
                        key="copy"
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        disabled={!copySourceRegister}
                        loading={copyingFromSource}
                        onClick={handleExecuteCopyFromSource}
                    >
                        Tiến hành sao chép
                    </Button>
                ]}
            >
                <div style={{ marginBottom: 16 }}>
                    <Row gutter={12} align="bottom">
                        <Col span={10}>
                            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Khối thu nguồn:</div>
                            <Select
                                value={copySourceBlockId}
                                onChange={(val) => {
                                    setCopySourceBlockId(val);
                                    setCopySourceRegister(null);
                                    setHasSearchedCopySource(false);
                                }}
                                style={{ width: '100%' }}
                                placeholder="Chọn khối thu nguồn"
                            >
                                {blocks.map((b) => (
                                    <Option key={b.id} value={b.id}>
                                        {b.name} ({b.code})
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={5}>
                            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Tháng nguồn:</div>
                            <Select
                                value={copySourceMonth}
                                onChange={(val) => {
                                    setCopySourceMonth(val);
                                    setCopySourceRegister(null);
                                    setHasSearchedCopySource(false);
                                }}
                                style={{ width: '100%' }}
                            >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                    <Option key={m} value={m}>
                                        Tháng {m}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={5}>
                            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Năm nguồn:</div>
                            <InputNumber
                                min={2020}
                                max={2035}
                                value={copySourceYear}
                                onChange={(val) => {
                                    if (val) {
                                        setCopySourceYear(val);
                                        setCopySourceRegister(null);
                                        setHasSearchedCopySource(false);
                                    }
                                }}
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col span={4}>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                loading={loadingCopySource}
                                onClick={() => handleFindCopySource(copySourceBlockId, copySourceMonth, copySourceYear)}
                                style={{ width: '100%' }}
                            >
                                Tìm
                            </Button>
                        </Col>
                    </Row>
                </div>

                <Divider style={{ margin: '16px 0' }} />

                <Spin spinning={loadingCopySource}>
                    {copySourceRegister ? (
                        <div style={{ background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 6, padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                <span style={{ fontWeight: 600 }}>{copySourceRegister.name}</span>
                                <Tag color="blue">Tháng {copySourceRegister.month}/{copySourceRegister.year}</Tag>
                            </div>
                            <div style={{ color: '#595959', fontSize: 13 }}>
                                Sẽ sao chép sang <b>{blocks.find(b => b.id === selectedBlockId)?.name}</b>:{' '}
                                Tổng mức thu <b style={{ color: '#1677ff' }}>{formatCurrency(copySourceRegister.levels?.[0]?.totalAmount)}</b> —{' '}
                                <b>{(copySourceRegister.levels?.[0]?.items || []).filter(i => i.isDefault !== false).length}</b> khoản thu mặc định
                            </div>
                        </div>
                    ) : hasSearchedCopySource ? (
                        <Empty
                            description="Không tìm thấy phiếu đăng ký nhanh nào của khối này ở Tháng đã chọn"
                            style={{ margin: '16px 0' }}
                        />
                    ) : (
                        <div style={{ textAlign: 'center', color: '#8c8c8c', padding: '16px 0' }}>
                            Chọn khối, kỳ nguồn và bấm "Tìm" để kiểm tra cấu hình trước khi sao chép
                        </div>
                    )}
                </Spin>
            </Modal>
        </div>
    );
};

export default QuickRegisterPage;
